import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { AuthProvider } from "@/components/auth/AuthProvider";
import Home from "./pages/Home";
import Auth from "./pages/Auth";
import Lenses from "./pages/Lenses";
import Chat from "./pages/Chat";
import Admin from "./pages/Admin";
import BecomeExpert from "./pages/BecomeExpert";
import ForExpert from "./pages/ForExpert";
import ConversationManager from "./pages/ConversationManager";
import MyConversations from "./pages/MyConversations";
import WaitingList from "./pages/WaitingList";
import ResetPassword from "./pages/ResetPassword";
import AuthorsLanding1 from "./pages/AuthorsLanding1";
import AuthorsLanding2 from "./pages/AuthorsLanding2";
import Pricing from "./pages/Pricing";
import LensProfile from "./pages/LensProfile";
import Account from "./pages/Account";
import ExpertBoards from "./pages/ExpertBoards";
import OperatingSystems from "./pages/OperatingSystems";
import OperatingSystemRun from "./pages/OperatingSystemRun";
import OperatingSystemViewer from "./pages/OperatingSystemViewer";
import Templates from "./pages/Templates";
import Wizard from "./pages/Wizard";
import GuideArticle from "./pages/GuideArticle";
import LegalHoldingPage from "./pages/LegalHoldingPage";
import PricingPowerAdvisorPage, { PricingPowerAdvisorSupportPage } from "./pages/PricingPowerAdvisorPlugin";
import PricingPowerAdvisorLegalPage from "./pages/PricingPowerAdvisorLegalPage";
import OperatingSystemVerificationPreview from "./components/operating-systems/OperatingSystemVerificationPreview";
import { SupportProvider } from "@/components/support/SupportProvider";
import { SupportWidget } from "@/components/support/SupportWidget";
import { AnalyticsRouteTracker } from "@/components/analytics/AnalyticsRouteTracker";
import { SeoRoutePolicy } from "@/components/seo/SeoRoutePolicy";
import { OperatingSystemJobNotifier } from "@/components/operating-systems/OperatingSystemJobNotifier";
import { OperatingSystemPreviewCacheProvider } from "@/components/operating-systems/OperatingSystemPreviewCache";
import { RecoveryBoundary } from "@/components/RecoveryBoundary";
import { ServiceIncidentBanner } from "@/components/ServiceIncidentBanner";

import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

const ApplicationDetail = () => {
  const { osId } = useParams();
  return <RecoveryBoundary key={osId}><OperatingSystemViewer /></RecoveryBoundary>;
};

const LegacyApplicationRedirect = ({ run = false }: { run?: boolean }) => {
  const { osId } = useParams();
  const location = useLocation();
  const appPath = osId ? `/${encodeURIComponent(osId)}${run ? "/run" : ""}` : "";
  return <Navigate to={`/applications${appPath}${location.search}${location.hash}`} replace />;
};

const LegacyBetaAccessRedirect = () => {
  const location = useLocation();
  return <Navigate to={`/beta_access${location.search}${location.hash}`} replace />;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ServiceIncidentBanner />
          <SupportProvider>
            <OperatingSystemPreviewCacheProvider>
              <SeoRoutePolicy />
              <AnalyticsRouteTracker />
              <OperatingSystemJobNotifier />
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/lenses" element={<Lenses />} />
                <Route path="/guides/:slug" element={<GuideArticle />} />
                <Route path="/privacy" element={<LegalHoldingPage document="Privacy Policy" />} />
                <Route path="/terms" element={<LegalHoldingPage document="Terms of Service" />} />
                <Route path="/plugins/pricing-power-advisor" element={<PricingPowerAdvisorPage />} />
                <Route path="/plugins/pricing-power-advisor/support" element={<PricingPowerAdvisorSupportPage />} />
                <Route path="/plugins/pricing-power-advisor/privacy" element={<PricingPowerAdvisorLegalPage document="privacy" />} />
                <Route path="/plugins/pricing-power-advisor/terms" element={<PricingPowerAdvisorLegalPage document="terms" />} />
                <Route path="/expert-boards" element={<ExpertBoards />} />
                <Route path="/applications" element={<OperatingSystems />} />
                <Route path="/templates" element={<Templates />} />
                {import.meta.env.DEV && (
                  <>
                    <Route path="/applications/verification-building" element={<OperatingSystemVerificationPreview state="building" />} />
                    <Route path="/applications/verification-ready/run" element={<OperatingSystemVerificationPreview state="ready" />} />
                    <Route path="/operating-systems/verification-building" element={<Navigate to="/applications/verification-building" replace />} />
                    <Route path="/operating-systems/verification-ready/run" element={<Navigate to="/applications/verification-ready/run" replace />} />
                  </>
                )}
                <Route path="/applications/:osId/run" element={<OperatingSystemRun />} />
                <Route path="/applications/:osId" element={<ApplicationDetail />} />
                <Route path="/operating-systems" element={<LegacyApplicationRedirect />} />
                <Route path="/operating-systems/:osId/run" element={<LegacyApplicationRedirect run />} />
                <Route path="/operating-systems/:osId" element={<LegacyApplicationRedirect />} />
                <Route path="/wizard" element={<Wizard />} />
                <Route path="/chat/new" element={<Chat />} />
                <Route path="/chat/:conversationId" element={<Chat />} />
                <Route path="/board/:conversationId" element={<Chat boardMode />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/become_expert" element={<BecomeExpert />} />
                <Route path="/for_expert" element={<ForExpert />} />
                <Route path="/expert" element={<Navigate to="/become_expert" replace />} />
                <Route path="/expert2" element={<Navigate to="/for_expert" replace />} />
                <Route path="/conversations" element={<ConversationManager />} />
                <Route path="/my-conversations" element={<MyConversations />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/beta_access" element={<WaitingList />} />
                <Route path="/waiting-list" element={<LegacyBetaAccessRedirect />} />
                <Route path="/waitlist" element={<LegacyBetaAccessRedirect />} />
                <Route path="/for_authors" element={<AuthorsLanding2 />} />
                <Route path="/for_authors1" element={<AuthorsLanding1 />} />
                <Route path="/lens/:methodId" element={<LensProfile />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/account" element={<Account />} />
                <Route path="/upgrade" element={<Navigate to="/pricing" replace />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </OperatingSystemPreviewCacheProvider>
            <SupportWidget />
          </SupportProvider>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
